import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'

import { useNotificationBar } from 'context/notificationContext'
import { getErrorResponse } from 'services/util'
import { QueryKeys } from 'hooks/_query_keys'
import {
    Article,
    ArticlePayload,
    connectedContentFromForm,
    seoFieldsFromForm,
    toArticlePayload,
    unwrapResponse,
    useCreateArticle,
    usePublishArticle,
} from 'hooks/article'
import ArticleForm, { ArticleFormHelpers, SubmitIntent } from './Articleform'
import { ArticleFormValues } from './validation'

const CreateArticle: React.FC = () => {
    const navigate = useNavigate()
    const notification = useNotificationBar()
    const queryClient = useQueryClient()
    const createArticle = useCreateArticle()
    const publishArticle = usePublishArticle()
    const [busy, setBusy] = useState(false)

    const onSubmit = async (
        values: ArticleFormValues,
        intent: SubmitIntent,
        { setError }: ArticleFormHelpers
    ) => {
        const slug = values.slug?.trim()
        if (intent === 'publish') {
            if (!values.seoTitle?.trim()) {
                setError('seoTitle', { message: 'SEO title is required before publishing' })
                return
            }
            if (!values.metaDescription?.trim()) {
                setError('metaDescription', { message: 'Meta description is required before publishing' })
                return
            }
        }

        const payload: ArticlePayload = {
            title: values.title.trim(),
            ...(slug ? { slug } : {}), // omitted -> backend derives it from the title
            short_description: values.short_description.trim(),
            author: values.author.trim(),
            content_html: values.content_html || '',
            status: 'draft',
            ...seoFieldsFromForm(values),
            ...connectedContentFromForm(values.connectedContent),
        }

        setBusy(true)
        let saved: Article | null = null

        try {
            // POST /articles always creates a draft
            saved = unwrapResponse(await createArticle.mutateAsync({ data: payload })) as Article

            if (intent === 'publish') {
                // POST /articles/:id/publish with the full payload + version we just received
                await publishArticle.mutateAsync({
                    id: saved.id,
                    data: toArticlePayload(saved, { status: 'published' }),
                })
            }

            queryClient.invalidateQueries([QueryKeys.ARTICLES])
            notification.push({
                title: intent === 'publish' ? 'Article published' : 'Article created',
                type: 'success',
                message:
                    intent === 'publish'
                        ? 'The article is now published.'
                        : 'The article was saved as a draft.',
            })
            navigate('/articles')
        } catch (err: any) {
            queryClient.invalidateQueries([QueryKeys.ARTICLES])

            // the draft exists but publishing failed -> continue in the editor
            if (saved) {
                notification.push({
                    title: 'Saved as draft',
                    type: 'error',
                    message: 'The article was created but could not be published. You can retry from the editor.',
                })
                navigate(`/articles/edit/${saved.id}`)
                return
            }

            if (err?.response?.status === 409) {
                setError('slug', { message: 'This custom URL is already in use. Choose another one.' })
                return
            }

            notification.push(getErrorResponse(err))
        } finally {
            setBusy(false)
        }
    }

    return <ArticleForm mode="create" isSubmitting={busy} onSubmit={onSubmit} />
}

export default CreateArticle